import { useEffect, useState } from 'react'
import { useAuth } from '../lib/useAuth'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { User, Shield } from 'lucide-react'

// ViewSwitcher
// =================================================================
// Renders the Player ⇄ Admin nav button. Visible when the user has any
// dashboard-eligible role:
//   - global users.role of m55_admin or gm, OR
//   - per-event event_players.event_role of gm or co_gm in any event, OR
//   - org_members row with org_role in (org_owner, org_admin, org_gm).
//
// Bug 41: org_owner / org_admin / org_gm all qualify now — without it an
// org owner whose users.role is still 'player' couldn't toggle back to
// admin once they landed on /play.
// =================================================================
export default function ViewSwitcher({ currentView, compact = false }) {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [hasDualRole, setHasDualRole] = useState(false)

  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    const check = async () => {
      // Parallel: any gm/co_gm event_players row OR any org_owner /
      // org_admin / org_gm org_members row. Either confirms dual-role.
      const [{ data: epRows }, { data: omRows }] = await Promise.all([
        // Bug 61 re-open: event_players has no `id` column on prod
        // (composite PK on event_id + user_id; dev had a surrogate id
        // added at some point and the cutover didn't include it). Selecting
        // user_id keeps the existence check working in both envs.
        supabase.from('event_players').select('user_id')
          .eq('user_id', user.id)
          .in('event_role', ['gm', 'co_gm']).limit(1),
        supabase.from('org_members').select('user_id')
          .eq('user_id', user.id)
          .in('org_role', ['org_owner', 'org_admin', 'org_gm']).limit(1),
      ])
      if (!cancelled) setHasDualRole(!!epRows?.[0] || !!omRows?.[0])
    }
    check()
    return () => { cancelled = true }
  }, [user?.id])

  // Platform staff (platform_role or legacy users.role='m55_admin') or
  // legacy users.role='gm' get the switcher unconditionally; per-event
  // gm/co_gm and per-org owner/admin/gm get it via the lookups above.
  const isPlatformOrGlobalGm =
    !!profile?.platform_role
    || profile?.role === 'm55_admin'
    || profile?.role === 'gm'
  if (!isPlatformOrGlobalGm && !hasDualRole) return null

  const isGM = currentView === 'gm'

  if (compact) {
    return (
      <button
        onClick={() => navigate(isGM ? '/play' : '/dashboard')}
        title={isGM ? 'Switch to Player View' : 'Switch to Admin View'}
        style={{
          width: '32px',
          height: '32px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          background: 'rgba(154, 102, 255, .15)',
          color: '#b380ff',
          border: '1px solid rgba(154, 102, 255, .35)',
          flexShrink: 0,
        }}
      >
        {isGM ? <User size={16} /> : <Shield size={16} />}
      </button>
    )
  }

  return (
    <button onClick={() => navigate(isGM ? '/play' : '/dashboard')}
      style={{
        // Hardening Track 6.2 — 44x44 minimum tap target. Previously
        // 5px×12px padding on an 11px font made the hit area ~24px
        // tall, well under the WCAG 2.5.5 spec and a real problem on
        // mobile.
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        minHeight: '44px',
        minWidth: '44px',
        fontSize: '12px',
        fontWeight: 600,
        letterSpacing: '0.08em',
        padding: '8px 16px',
        cursor: 'pointer',
        background: 'rgba(154, 102, 255, .15)',
        color: '#b380ff',
        border: '1px solid rgba(154, 102, 255, .35)',
        textShadow: 'none',
        fontFamily: 'inherit',
      }}
    >
      {/* Bug 42: short labels to keep the toggle compact on mobile nav. */}
      {isGM ? 'Player' : 'Admin'}
    </button>
  )
}
